import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { ProgramType } from "../../../domain/treatmentPlan.interface";

/**
 * How a plan's configuration is authored.
 *
 * Deliberately NOT a third `programType`. A plan built by conversation is an
 * ordinary WEEKLY_CALENDAR plan — the same treatment config, the same
 * enrollment and runtime behaviour — so this is a property of the editing
 * session, not of the saved plan. Making it a program type would change the
 * data model for what is only an affordance.
 */
export type AuthoringMode = "MANUAL" | "ASSISTANT";

export interface PlanMode {
	programType: ProgramType;
	authoringMode: AuthoringMode;
}

interface PlanModeOption extends PlanMode {
	id: string;
	label: string;
	description: string;
	icon: React.ReactNode;
	badge?: string;
}

const OPTIONS: PlanModeOption[] = [
	{
		id: "MANUAL",
		programType: "WEEKLY_CALENDAR",
		authoringMode: "MANUAL",
		label: "Manual Plan (Legacy)",
		description:
			"Configure behaviours, rewards, visit schedules and plan options by hand.",
		icon: <CalendarMonthIcon sx={{ fontSize: 40 }} />,
	},
	{
		id: "ASSISTANT",
		programType: "WEEKLY_CALENDAR",
		authoringMode: "ASSISTANT",
		label: "Treatment Config",
		description:
			"Describe the program in plain English. The assistant builds the same config and checks it as you go.",
		icon: <ChatBubbleOutlineIcon sx={{ fontSize: 40 }} />,
		badge: "AI-Powered",
	},
	{
		id: "SMART_PROGRAM",
		programType: "SMART_PROGRAM",
		authoringMode: "ASSISTANT",
		label: "Smart Program",
		description:
			"AI-powered workflow configuration. Describe your program and get a validated workflow.",
		icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
		badge: "AI-Powered",
	},
];

interface PlanModeSelectorProps {
	value: PlanMode;
	onChange: (mode: PlanMode) => void;
	/**
	 * The program type the plan was saved with, once it has been saved.
	 *
	 * Program type is fixed at creation — it decides how the plan behaves at
	 * enrolment and run time, so changing it under an existing plan is not a
	 * UI concern. Authoring mode is not fixed: the manual form and the
	 * assistant produce the same treatment config, so moving between them is
	 * always allowed.
	 */
	lockedProgramType?: ProgramType | null;
}

const PlanModeSelector: React.FC<PlanModeSelectorProps> = ({
	value,
	onChange,
	lockedProgramType,
}) => {
	const isLocked = (option: PlanModeOption): boolean =>
		!!lockedProgramType && option.programType !== lockedProgramType;

	return (
		<Box sx={{ mb: 3 }}>
			<Typography variant="subtitle2" sx={{ mb: 1 }}>
				Program Type
			</Typography>
			<Box sx={{ display: "flex", gap: 2 }}>
				{OPTIONS.map((option) => {
					const isSelected =
						value.programType === option.programType &&
						value.authoringMode === option.authoringMode;
					const locked = isLocked(option);

					const card = (
						<Card
							key={option.id}
							data-testid={`plan-mode-${option.id}`}
							aria-disabled={locked}
							onClick={() =>
								!locked &&
								onChange({
									programType: option.programType,
									authoringMode: option.authoringMode,
								})
							}
							sx={{
								flex: 1,
								cursor: locked ? "not-allowed" : "pointer",
								opacity: locked ? 0.5 : 1,
								border: (theme) =>
									isSelected
										? `2px solid ${theme.palette.primary.main}`
										: "1px solid rgba(0,0,0,0.12)",
							}}
						>
							<CardContent sx={{ textAlign: "center" }}>
								{option.icon}
								<Typography variant="subtitle1" sx={{ mt: 1 }}>
									{option.label}
								</Typography>
								{option.badge ? (
									<Chip
										size="small"
										label={option.badge}
										color="primary"
										sx={{ mt: 0.5 }}
									/>
								) : null}
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mt: 1 }}
								>
									{option.description}
								</Typography>
							</CardContent>
						</Card>
					);

					// Say WHY it cannot be picked, rather than showing a dead card.
					return locked ? (
						<Tooltip
							key={option.id}
							title="Program type is set when the plan is created and cannot be changed afterwards."
						>
							<Box sx={{ flex: 1, display: "flex" }}>{card}</Box>
						</Tooltip>
					) : (
						card
					);
				})}
			</Box>
		</Box>
	);
};

export default PlanModeSelector;
